/**
 * DSL-to-Tools Master Orchestrator — calls all generators and merges results.
 *
 * Provides:
 *   - generateToolsFromBlock(block, flows) — single block
 *   - generateToolsFromPage(page, blockMap, flowMap) — all blocks on a page
 *   - generateToolsFromApp(app, blockMap, flowMap) — entire app + navigation
 */

import type { CogoApp, CogoPage, CogoBlockDef, CogoFlow } from "@cogo/types"
import type { WebMCPTool } from "../core/tool-registry"

import { generateFormTools } from "./form-tools"
import { generateActionFlowTools } from "./action-flow-tools"
import { generateDataTableTools } from "./data-table-tools"
import { generateNavigationTools } from "./navigation-tools"
import { generateCrudTools } from "./crud-tools"
import { generateCartTools } from "./cart-tools"

// ── Block-level ────────────────────────────────────────────

export function generateToolsFromBlock(
  block: CogoBlockDef,
  flows: Record<string, CogoFlow> = {},
  narratives?: Record<string, { effect: string }>,
): WebMCPTool[] {
  const tools: WebMCPTool[] = []

  // 1. Form tools (from UI tree)
  tools.push(...generateFormTools(block, narratives))

  // 2. Action flow tools (from flows)
  if (Object.keys(flows).length > 0) {
    tools.push(...generateActionFlowTools(block, flows))
  }

  // 3. Data table tools (from UI tree)
  tools.push(...generateDataTableTools(block, narratives))

  // 4. CRUD tools (from flows)
  if (Object.keys(flows).length > 0) {
    tools.push(...generateCrudTools(block, flows))
  }

  // 5. Cart tools (from flows)
  if (Object.keys(flows).length > 0) {
    tools.push(...generateCartTools(block, flows))
  }

  // Deduplicate by tool name (last wins)
  return deduplicateTools(tools)
}

// ── Page-level ─────────────────────────────────────────────

export function generateToolsFromPage(
  page: CogoPage,
  blockMap: Record<string, CogoBlockDef>,
  flowMap: Record<string, Record<string, CogoFlow>>,
  narratives?: Record<string, { effect: string }>,
): WebMCPTool[] {
  const tools: WebMCPTool[] = []

  const pageBlocks = page.blocks ?? []
  for (const blockName of pageBlocks) {
    const block = blockMap[blockName]
    if (!block) continue

    const flows = flowMap[blockName] ?? {}
    const blockTools = generateToolsFromBlock(block, flows, narratives)

    // Namespace tool names with block name if multiple blocks
    if (pageBlocks.length > 1) {
      for (const tool of blockTools) {
        // Only prefix if not already prefixed
        if (!tool.name.includes(blockName)) {
          tool.metadata = {
            ...tool.metadata,
            blockName: blockName,
          }
        }
      }
    }

    tools.push(...blockTools)
  }

  return deduplicateTools(tools)
}

// ── App-level ──────────────────────────────────────────────

export function generateToolsFromApp(
  app: CogoApp,
  blockMap: Record<string, CogoBlockDef>,
  flowMap: Record<string, Record<string, CogoFlow>>,
  narratives?: Record<string, { effect: string }>,
): WebMCPTool[] {
  const tools: WebMCPTool[] = []

  // 1. Navigation tool
  tools.push(...generateNavigationTools(app, narratives))

  // 2. Per-page tools
  for (const page of app.pages) {
    tools.push(...generateToolsFromPage(page, blockMap, flowMap, narratives))
  }

  return deduplicateTools(tools)
}

// ── Helpers ────────────────────────────────────────────────

function deduplicateTools(tools: WebMCPTool[]): WebMCPTool[] {
  const map = new Map<string, WebMCPTool>()
  for (const tool of tools) {
    map.set(tool.name, tool)
  }
  return Array.from(map.values())
}
